/**
 * Physics Zone nodes — gravity, wind, buoyancy zones.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// GRAVITY ZONES
// ============================================================================

export const SetGravityZoneStrength: NodeBehaviour = {
  id: 'zones.setGravityStrength',
  name: 'Set Gravity Zone Strength',
  category: 'Physics Zones',
  color: '#9b59b6',
  icon: 'Globe',
  description: 'Sets the strength of a gravity zone.',
  keywords: ['gravity', 'pull', 'force'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('strength', 'Strength', 'f32', 9.81),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setGravityZoneStrength', [ctx.resolveInput('entity'), ctx.resolveInput('strength')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setGravityZoneStrength', eid, ctx.getInput<number>('strength'));
  },
};

export const SetGravityZoneVector: NodeBehaviour = {
  id: 'zones.setGravityVector',
  name: 'Set Gravity Zone Vector',
  category: 'Physics Zones',
  color: '#9b59b6',
  icon: 'Globe',
  description: 'Sets the gravity direction of a zone (for Override/Additive modes).',
  keywords: ['gravity', 'direction', 'force'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Gravity X', 'f32', 0),
    input('y', 'Gravity Y', 'f32', -9.81),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setGravityZoneVector', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setGravityZoneVector', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const EnableGravityZone: NodeBehaviour = {
  id: 'zones.enableGravity',
  name: 'Enable Gravity Zone',
  category: 'Physics Zones',
  color: '#9b59b6',
  icon: 'Globe',
  description: 'Enables or disables a gravity zone.',
  keywords: ['gravity', 'toggle', 'on', 'off'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('enabled', 'Enabled', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:enableGravityZone', [ctx.resolveInput('entity'), ctx.resolveInput('enabled')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:enableGravityZone', eid, ctx.getInput<boolean>('enabled'));
  },
};

// ============================================================================
// WIND ZONES
// ============================================================================

export const SetWindZoneStrength: NodeBehaviour = {
  id: 'zones.setWindStrength',
  name: 'Set Wind Zone Strength',
  category: 'Physics Zones',
  color: '#3498db',
  icon: 'Wind',
  description: 'Sets the strength of a wind zone.',
  keywords: ['wind', 'blow', 'force'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('strength', 'Strength', 'f32', 10),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setWindZoneStrength', [ctx.resolveInput('entity'), ctx.resolveInput('strength')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setWindZoneStrength', eid, ctx.getInput<number>('strength'));
  },
};

export const SetWindZoneDirection: NodeBehaviour = {
  id: 'zones.setWindDirection',
  name: 'Set Wind Zone Direction',
  category: 'Physics Zones',
  color: '#3498db',
  icon: 'Wind',
  description: 'Sets the direction of a wind zone.',
  keywords: ['wind', 'blow', 'direction'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Direction X', 'f32', 1),
    input('y', 'Direction Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setWindZoneDirection', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setWindZoneDirection', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const SetWindZoneTurbulence: NodeBehaviour = {
  id: 'zones.setWindTurbulence',
  name: 'Set Wind Turbulence',
  category: 'Physics Zones',
  color: '#3498db',
  icon: 'Wind',
  description: 'Sets the turbulence of a wind zone.',
  keywords: ['wind', 'random', 'noise'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('turbulence', 'Turbulence', 'f32', 0.5),
    input('frequency', 'Frequency', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setWindZoneTurbulence', [
      ctx.resolveInput('entity'),
      ctx.resolveInput('turbulence'),
      ctx.resolveInput('frequency'),
    ], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setWindZoneTurbulence', eid, ctx.getInput<number>('turbulence'), ctx.getInput<number>('frequency'));
  },
};

export const EnableWindZone: NodeBehaviour = {
  id: 'zones.enableWind',
  name: 'Enable Wind Zone',
  category: 'Physics Zones',
  color: '#3498db',
  icon: 'Wind',
  description: 'Enables or disables a wind zone.',
  keywords: ['wind', 'toggle', 'on', 'off'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('enabled', 'Enabled', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:enableWindZone', [ctx.resolveInput('entity'), ctx.resolveInput('enabled')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:enableWindZone', eid, ctx.getInput<boolean>('enabled'));
  },
};

// ============================================================================
// BUOYANCY ZONES
// ============================================================================

export const SetBuoyancyDensity: NodeBehaviour = {
  id: 'zones.setBuoyancyDensity',
  name: 'Set Buoyancy Density',
  category: 'Physics Zones',
  color: '#1abc9c',
  icon: 'Droplet',
  description: 'Sets the fluid density of a buoyancy zone.',
  keywords: ['water', 'float', 'liquid'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('density', 'Density', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBuoyancyDensity', [ctx.resolveInput('entity'), ctx.resolveInput('density')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setBuoyancyDensity', eid, ctx.getInput<number>('density'));
  },
};

export const SetBuoyancyDrag: NodeBehaviour = {
  id: 'zones.setBuoyancyDrag',
  name: 'Set Buoyancy Drag',
  category: 'Physics Zones',
  color: '#1abc9c',
  icon: 'Droplet',
  description: 'Sets the drag coefficients of a buoyancy zone.',
  keywords: ['water', 'resistance', 'friction'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('linear', 'Linear Drag', 'f32', 1),
    input('angular', 'Angular Drag', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBuoyancyDrag', [
      ctx.resolveInput('entity'),
      ctx.resolveInput('linear'),
      ctx.resolveInput('angular'),
    ], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setBuoyancyDrag', eid, ctx.getInput<number>('linear'), ctx.getInput<number>('angular'));
  },
};

export const SetBuoyancyFlow: NodeBehaviour = {
  id: 'zones.setBuoyancyFlow',
  name: 'Set Buoyancy Flow',
  category: 'Physics Zones',
  color: '#1abc9c',
  icon: 'Droplet',
  description: 'Sets the current/flow direction of a buoyancy zone.',
  keywords: ['water', 'current', 'stream'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Flow X', 'f32', 0),
    input('y', 'Flow Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBuoyancyFlow', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setBuoyancyFlow', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

export const EnableBuoyancyZone: NodeBehaviour = {
  id: 'zones.enableBuoyancy',
  name: 'Enable Buoyancy Zone',
  category: 'Physics Zones',
  color: '#1abc9c',
  icon: 'Droplet',
  description: 'Enables or disables a buoyancy zone.',
  keywords: ['water', 'toggle', 'on', 'off'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('enabled', 'Enabled', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:enableBuoyancyZone', [ctx.resolveInput('entity'), ctx.resolveInput('enabled')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:enableBuoyancyZone', eid, ctx.getInput<boolean>('enabled'));
  },
};

// ============================================================================
// SURFACE EFFECTORS
// ============================================================================

export const SetSurfaceEffectorSpeed: NodeBehaviour = {
  id: 'zones.setSurfaceSpeed',
  name: 'Set Surface Speed',
  category: 'Physics Zones',
  color: '#e74c3c',
  icon: 'ArrowRight',
  description: 'Sets the conveyor belt speed of a surface effector.',
  keywords: ['conveyor', 'belt', 'slide'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('speed', 'Speed', 'f32', 5),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSurfaceEffectorSpeed', [ctx.resolveInput('entity'), ctx.resolveInput('speed')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSurfaceEffectorSpeed', eid, ctx.getInput<number>('speed'));
  },
};

export const SetSurfaceEffectorForce: NodeBehaviour = {
  id: 'zones.setSurfaceForce',
  name: 'Set Surface Force',
  category: 'Physics Zones',
  color: '#e74c3c',
  icon: 'ArrowRight',
  description: 'Sets the force applied by a surface effector.',
  keywords: ['conveyor', 'push', 'acceleration'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('force', 'Force', 'f32', 10),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSurfaceEffectorForce', [ctx.resolveInput('entity'), ctx.resolveInput('force')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSurfaceEffectorForce', eid, ctx.getInput<number>('force'));
  },
};

// ============================================================================
// ZONE QUERIES
// ============================================================================

export const IsInGravityZone: NodeBehaviour = {
  id: 'zones.isInGravity',
  name: 'Is In Gravity Zone',
  category: 'Physics Zones',
  isPure: true,
  color: '#9b59b6',
  icon: 'Globe',
  description: 'Checks if an entity is inside any gravity zone.',
  keywords: ['gravity', 'inside', 'check'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('inside', 'Is Inside', 'bool'),
    output('zone', 'Zone Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isInGravityZone', [ctx.resolveInput('entity')], 'zoneResult');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:isInGravityZone', eid) as { inside: boolean; zone: number } | null;
    ctx.setOutput('inside', r?.inside ?? false);
    ctx.setOutput('zone', r?.zone ?? -1);
  },
};

export const IsInWindZone: NodeBehaviour = {
  id: 'zones.isInWind',
  name: 'Is In Wind Zone',
  category: 'Physics Zones',
  isPure: true,
  color: '#3498db',
  icon: 'Wind',
  description: 'Checks if an entity is inside any wind zone.',
  keywords: ['wind', 'inside', 'check'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('inside', 'Is Inside', 'bool'),
    output('zone', 'Zone Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isInWindZone', [ctx.resolveInput('entity')], 'zoneResult');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:isInWindZone', eid) as { inside: boolean; zone: number } | null;
    ctx.setOutput('inside', r?.inside ?? false);
    ctx.setOutput('zone', r?.zone ?? -1);
  },
};

export const IsInBuoyancyZone: NodeBehaviour = {
  id: 'zones.isInBuoyancy',
  name: 'Is In Buoyancy Zone',
  category: 'Physics Zones',
  isPure: true,
  color: '#1abc9c',
  icon: 'Droplet',
  description: 'Checks if an entity is inside any buoyancy zone (water).',
  keywords: ['water', 'inside', 'submerged'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('inside', 'Is Inside', 'bool'),
    output('depth', 'Submerge Depth', 'f32'),
    output('zone', 'Zone Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isInBuoyancyZone', [ctx.resolveInput('entity')], 'buoyancyResult');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    const r = ctx.bridge.call('bridge:isInBuoyancyZone', eid) as { inside: boolean; depth: number; zone: number } | null;
    ctx.setOutput('inside', r?.inside ?? false);
    ctx.setOutput('depth', r?.depth ?? 0);
    ctx.setOutput('zone', r?.zone ?? -1);
  },
};

export const ZONES_NODES: readonly NodeBehaviour[] = [
  SetGravityZoneStrength, SetGravityZoneVector, EnableGravityZone,
  SetWindZoneStrength, SetWindZoneDirection, SetWindZoneTurbulence, EnableWindZone,
  SetBuoyancyDensity, SetBuoyancyDrag, SetBuoyancyFlow, EnableBuoyancyZone,
  SetSurfaceEffectorSpeed, SetSurfaceEffectorForce,
  IsInGravityZone, IsInWindZone, IsInBuoyancyZone,
];
